import { Ref, useImperativeHandle, useMemo } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import { useTranslation } from "@coworker/locales";
import TitledTextArea from "@coworker/apprestructured/src/task-management/components/TitledTextArea/TitledTextArea";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import styles from "./TitledTextAreaCreatorFragment.module.css";

const MAX_TEXT_AREA_LENGTH = 1000;

export type TitledTextAreaCreatorFragmentConfigProps = {
  field: string;
  title?: string;
  label: string;
  subtitle?: string;
  rows?: number;
  validationErrorTranslationKey?: string;
};

type TitledTextAreaCreatorFragmentProps = {
  configProps: TitledTextAreaCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function TitledTextAreaCreatorFragment({
  configProps,
  commonProps,
  ref,
}: TitledTextAreaCreatorFragmentProps) {
  const { field, title, label, subtitle, rows, validationErrorTranslationKey } =
    configProps;
  const { task, onTaskChange, blockInput, showValidation } = commonProps;
  const value = getTypedValue<string>(getByPath(task, field)) || "";

  const { t } = useTranslation();

  const isEmpty = !isLoading(value) && value.trim().length === 0;

  const error = useMemo(() => {
    if (!isLoading(value) && value.length > MAX_TEXT_AREA_LENGTH) {
      return t("maximumCharactersLengthString", {
        count: MAX_TEXT_AREA_LENGTH,
      });
    }
    if (validationErrorTranslationKey && isEmpty && showValidation) {
      return t(validationErrorTranslationKey);
    }
    return null;
  }, [value, t, validationErrorTranslationKey, isEmpty, showValidation]);

  useImperativeHandle(ref, () => ({
    valid:
      !isLoading(value) &&
      !error &&
      !(validationErrorTranslationKey && isEmpty),
  }));

  if (isLoading(value)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <div className={styles["wrapper"]}>
      <TitledTextArea
        title={title}
        subtitle={subtitle}
        label={label}
        rows={rows}
        value={value}
        onChange={(newValue) => {
          if (
            newValue.length <= MAX_TEXT_AREA_LENGTH ||
            newValue.length < value.length
          ) {
            onTaskChange(setByPath(field, newValue));
          }
        }}
        error={error}
        disabled={blockInput}
      />
    </div>
  );
}

export default TitledTextAreaCreatorFragment;
